import React from 'react';
import classNames from "./CausationGraphView.module.css"
import { getUrlWithoutQueryOrHash, urlToURL } from '../utils/domains';
import { CallStackResolution } from './CallStackResolution';
import { getAllNodesCausingNode, createSubgraphReplicatingOnlyTheseNodes } from './CausationGraph';
import { CausationNodeAndRenderingData, filterGraphAndOrderNodesForRendering, annotateCausationNodesForRendering } from './CausationGraphRendering';
import { CausationNodeInitiator, CausationNodeCallFrame, CausationNode } from './CausationNode';
import { GetsDbEventsByIndex } from '../../State/EvidenceDB/DbEventCache';
import { observer } from 'mobx-react-lite';
import { DbRecordedEvent } from '../../State/EvidenceDB/DatabaseTypes';

const unicodeNonBreakingSpace = "\u00A0";

const constructGraphConnectorPrefix = ({
	tabDepth, firstAtDepth, tabDepthsOfNodesThatAreParentsOfThisNode, tabDepthsOfNodesWithChildrenToBeRenderedBelowThisNode,
}: Omit<CausationNodeAndRenderingData, 'node' | 'index'>) => {
	let graphConnectorPrefix = '';
	const earliestDepthConnectedToParent = Math.min(...tabDepthsOfNodesThatAreParentsOfThisNode);
	for (let i = 0; i < tabDepth -1; i++) {
		if (tabDepthsOfNodesThatAreParentsOfThisNode.has(i)) {
			graphConnectorPrefix += "┗"; // '╰' ;
		} else if (tabDepthsOfNodesWithChildrenToBeRenderedBelowThisNode.has(i)) {
			graphConnectorPrefix += i >= earliestDepthConnectedToParent ? "╋" : "┃";
		} else {
			graphConnectorPrefix += i >= earliestDepthConnectedToParent ? "━" : unicodeNonBreakingSpace;
		}
	}
	graphConnectorPrefix += firstAtDepth ? '┳' : '┣';
	return graphConnectorPrefix;
};
const CallFrameView = ({ node, callStackResolution = 'frame' }: { node: CausationNodeInitiator | CausationNodeCallFrame; callStackResolution?: CallStackResolution; }) => {
	switch (callStackResolution) {
		case 'frame':
			return (<>{`${node.url}:${node.lineNumber ?? ''}:${node.columnNumber ?? ''}`}</>);
		case 'url':
			return (<>{node.url}</>);
		case 'file':
			return (<>{getUrlWithoutQueryOrHash(node.url)}</>);
		case 'registered-domain':
		case 'origin':
			return (<>{urlToURL(node.url)?.origin}</>);
		default: return null;
	}
};

const CauseNodeContentsWithEventRaw = ({event, children}: React.PropsWithChildren<{
	node: CausationNode;
	event: DbRecordedEvent | number | undefined;
}>) => (
	<details style={{display: 'inline-block'}}>
		<summary>{ children }</summary>
		<pre>{ JSON.stringify(event, undefined, "  ")}
		</pre>
	</details>);

const CauseNodeContentsWithEvent = observer( ({node, getsDbEventsByIndex, children}: React.PropsWithChildren<{
	node: CausationNode;
	getsDbEventsByIndex: GetsDbEventsByIndex;
}>) => {
	const event = getsDbEventsByIndex.get(node.eventIndex) ?? node.eventIndex
	return (
		<CauseNodeContentsWithEventRaw {...{event, node}}>{children}</CauseNodeContentsWithEventRaw>
	);
});

const CauseNodeContentsView = ({ node, callStackResolution, getsDbEventsByIndex }: {
	node: CausationNode;
	getsDbEventsByIndex: GetsDbEventsByIndex;
	callStackResolution?: CallStackResolution;
}) => {
	switch (node.causeType) {
		case 'Network.requestWillBeSent':
			return (
				<CauseNodeContentsWithEvent {...{node, getsDbEventsByIndex}}>
					Fetches url {node.url}
				</CauseNodeContentsWithEvent>
		);
		case 'Network.requestWillBeSent:redirect':
			return (
				<CauseNodeContentsWithEvent {...{node, getsDbEventsByIndex}}>
					Redirects to: {node.redirectedToUrl}
				</CauseNodeContentsWithEvent>
			);
		case 'Page.frameAttached':
			return (
				<CauseNodeContentsWithEvent {...{node, getsDbEventsByIndex}}>
					Attaches window frame {node.frameId} to parent frame {node.parentFrameId}
				</CauseNodeContentsWithEvent>
				);
		// case 'page-frame-started-navigating':
		// 	return (<>Starts navigating to {cause.url} in window frame {cause.frameId} (event: {cause.eventIndex})</>);
		case 'Page.frameRequestedNavigation':
			return (
				<CauseNodeContentsWithEvent {...{node, getsDbEventsByIndex}}>
					Requests navigation to {node.url} in window frame {node.frameId}
				</CauseNodeContentsWithEvent>
			);
		case 'Page.navigatedWithinDocument':
			return (
				<CauseNodeContentsWithEvent {...{node, getsDbEventsByIndex}}>
					Navigates within document to {node.url} in window frame {node.frameId}
				</CauseNodeContentsWithEvent>
				);
		case 'initiator':
			return (<>Runs code initiated by {node.type} at <CallFrameView {...{ node, callStackResolution }} /></>);
		case 'call-frame':
			return (<>Runs code at <CallFrameView {...{ node, callStackResolution }} /></>);
		default: {
			return (
				<CauseNodeContentsWithEvent {...{node, getsDbEventsByIndex}}>
					Network request {node.causeType} (event: {node.eventIndex})
				</CauseNodeContentsWithEvent>
			);
		}
	}
};


const CauseNodeView = observer( ({ causationNodeAndRenderingData, getsDbEventsByIndex, callStackResolution }: {
	causationNodeAndRenderingData: CausationNodeAndRenderingData;
	callStackResolution?: CallStackResolution;
	getsDbEventsByIndex: GetsDbEventsByIndex;
}) => {
	const { node, index } = causationNodeAndRenderingData;
	const tabPrefix = constructGraphConnectorPrefix(causationNodeAndRenderingData);
	const indexStr = index.toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false });
	const prefix = `${tabPrefix}${indexStr}. `;
	return (<div className={classNames.CausationNodeContainerElement}>{ prefix }<CauseNodeContentsView {...{ node, callStackResolution, getsDbEventsByIndex }} /></div>);
});



export const CausationGraphView = observer( ({
	nodeCaused, getsDbEventsByIndex, callStackResolution = 'frame'
}: {
	nodeCaused: CausationNode;
	getsDbEventsByIndex: GetsDbEventsByIndex;
	callStackResolution?: CallStackResolution;
}) => {
	// Identify all the nodes that cause a node (including itself)
	// and then create a subgraph that includes only those nodes
 	const causalNodesWithinOriginalGraph = getAllNodesCausingNode(nodeCaused);
	const causalSubgraphNodes = createSubgraphReplicatingOnlyTheseNodes(causalNodesWithinOriginalGraph);
	const orderedFilteredSubgraphNodes = filterGraphAndOrderNodesForRendering(causalSubgraphNodes, callStackResolution);
	const orderedNodesWithRenderingData = annotateCausationNodesForRendering(orderedFilteredSubgraphNodes, callStackResolution);
	return (
		<div className={classNames.CausationGraphContainerElement}>{
			orderedNodesWithRenderingData.map(causationNodeAndRenderingData => (
			<CauseNodeView
				key={causationNodeAndRenderingData.node.causationNodeIndex}
				{...{ causationNodeAndRenderingData, callStackResolution, getsDbEventsByIndex }}
			/>)
		)}</div>
	);
});
